import { Link } from "react-router-dom";
import type { ContentItem } from "@/mocks/content";
import { formatDate, typeLabels } from "@/utils/content";

interface ContentCardProps {
  item: ContentItem;
  variant?: "default" | "horizontal" | "compact";
}

export default function ContentCard({ item, variant = "default" }: ContentCardProps) {
  if (variant === "horizontal") {
    return (
      <Link
        to={`/detail/${item.id}`}
        className="group flex flex-col sm:flex-row gap-4 md:gap-5 bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="w-full sm:w-56 h-44 sm:h-auto shrink-0 overflow-hidden bg-background-100">
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - 白峰美羽`}
            className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
          />
        </div>
        <div className="flex-1 p-4 md:p-5 flex flex-col">
          <div className="flex items-center gap-3 text-xs text-foreground-500 mb-2">
            <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900">
              {item.category}
            </span>
            <span>{formatDate(item.date)}</span>
          </div>
          <h3 className="text-base md:text-lg font-semibold text-foreground-950 font-heading leading-snug group-hover:text-primary-600 transition-colors">
            {item.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
            {item.summary}
          </p>
          <span className="mt-3 md:mt-auto pt-3 text-xs text-primary-600 inline-flex items-center gap-1">
            阅读全文
            <i className="ri-arrow-right-line"></i>
          </span>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link
        to={`/detail/${item.id}`}
        className="group flex gap-3 items-start cursor-pointer"
      >
        <div className="w-20 h-20 shrink-0 rounded-md overflow-hidden bg-background-100">
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - 白峰美羽`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {item.title}
          </h4>
          <p className="mt-1 text-xs text-foreground-500">{formatDate(item.date)}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${item.id}`}
      className="group flex flex-col bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors cursor-pointer h-full"
    >
      <div className="w-full h-52 overflow-hidden bg-background-100">
        <img
          src={item.cover}
          alt={item.title}
          title={`${item.title} - 白峰美羽`}
          className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500"
        />
      </div>
      <div className="flex-1 p-5 flex flex-col">
        <div className="flex items-center justify-between gap-3 text-xs text-foreground-500 mb-2">
          <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900">
            {item.category}
          </span>
          <span className="whitespace-nowrap">{formatDate(item.date)}</span>
        </div>
        <h3 className="text-base md:text-lg font-semibold text-foreground-950 font-heading leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
          {item.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-3">
          {item.summary}
        </p>
        <span className="mt-4 pt-3 border-t border-background-200 text-xs text-secondary-700 inline-flex items-center gap-1">
          {typeLabels[item.type]}
          <i className="ri-arrow-right-line"></i>
        </span>
      </div>
    </Link>
  );
}